import {
  Box,
  Flex,
  Modal,
  ScrollArea,
  Text
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";
import { getRelatedCxItem } from "../../../../../../Api";
import GongCallPart from "./GongCallPart";
import CallTopInfo from "./CallTopInfo";
import CallTitle from "./CallTitle";
import CallTextArea from "./CallTextArea";
import { IconArrowDown, IconArrowUp } from "@tabler/icons";
import {useFields} from "../../../../../../hooks/useFields";
import {useKnowledgeData} from "../../../../../../hooks/useKnowledgeData";
import { resolveEvidenceDisplayOrigin } from "../../../../../../utils/evidence/evidenceDisplayOrigin";
import { getOriginalItemViewLabel } from "../../../../../../utils/evidence/businessCategoryOriginalLabels";
import { getEvidenceDisplayTitle } from "../../../../../../utils";

const GongCall = ({ item, isLoadingMl = false }: { item: any; isLoadingMl?: boolean }) => {

  const auth0 = useAuth0();

  const { account } = useFields();
  const { members } = useKnowledgeData();
  const [opened, setOpened] = useState(false);
  const [gongCallParts, setGongCallParts] = useState<any[]>([]);
  const [internalUsers, setInternalUsers] = useState<any[]>([]);
  const [externalUsers, setExternalUsers] = useState<any[]>([]);
  const [callDuration, setCallDuration] = useState<string>("");
  const [refsIndexes, setRefsIndexes] = useState<any[]>([]);
  const refsArr = useRef<(HTMLDivElement | null)[]>([]);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  
  const scrollToElement = (index: number) => {

    if (refsArr.current[index]) {
      refsArr.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const scrollToTop = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const text = item?.text;
  // A blank title falls back to the first few words of the description — the text area
  // below still shows the full, untruncated text unchanged.
  const title = getEvidenceDisplayTitle(item?.title, text) || undefined;
  const cxItem = item?.cxItem;
  const system = cxItem?.system || "";
  const createdAtDate = cxItem?.createdAt
    ? new Date(cxItem?.createdAt)
    : new Date();
  const dateDisplay = moment(createdAtDate).format("MM-DD-YY");
  const origin = resolveEvidenceDisplayOrigin(item) || "";
  const callLinkLabel =
    getOriginalItemViewLabel(cxItem?.businessCategory) ||
    getOriginalItemViewLabel("call");
  const excerptsArr: any [] =  item?.excerpts || [];
  const excerptFirstPartMs =  excerptsArr.find((ex: any) => ex?.excerptFirstPartMs != null)?.excerptFirstPartMs || null;

  const loadGongCall = (cxItemIdForGong: string) => {
    getRelatedCxItem(cxItemIdForGong, auth0)
      .then((res) => {
        const parties = res?.parties || [];
        if (parties.length > 0) {
          parties.forEach((p: any) => {
            const mail = p?.emailAddress;
            const foundMember = members?.find((m: any) => m.email === mail);
            if (foundMember) {
              p.isBagelUser = true;
            }
          });
        }
       
        const internalParties = parties?.filter((p: any) => p.affiliation?.toLowerCase() === 'internal');
        const externalParties = parties?.filter((p: any) => !(p.affiliation?.toLowerCase() === 'internal'));
        setInternalUsers(internalParties);
        setExternalUsers(externalParties);
        const items = res?.parts || {};

        const itemsArray: any[] = Object.values(items);
        if (itemsArray?.length > 0) {
          const length = itemsArray?.length;
          const durationMs = itemsArray[length - 1]?.startMs || 0;
          const duration = formatDuration(durationMs);
          setCallDuration(duration);
          itemsArray.map((itemCall: any, index: number) => {
            excerptsArr.forEach((excerptItem: any) => {

              let indexForExcerpt = 0
              const excerptFromArrayFirstPartMs = excerptItem?.excerptFirstPartMs;
              const excerptFromArrayLastPartMs = excerptItem?.excerptLastPartMs;

              if (itemCall.startMs === excerptFromArrayFirstPartMs) {
                itemCall.isFirstInExcerpt = true;
                setRefsIndexes((prevIndexes) => [...prevIndexes, index]); 
              }

              if (
                itemCall.startMs >= excerptFromArrayFirstPartMs &&
                itemCall.startMs <= excerptFromArrayLastPartMs
              ) {
                itemCall.isExcerpt = true;
                itemCall.excerptIndex = indexForExcerpt;
                indexForExcerpt = indexForExcerpt++
              }

              if (itemCall.startMs === excerptFromArrayLastPartMs) {
                itemCall.isLastInExcerpt = true;
                
              }

            });

            const text = itemCall.text;
            const speakerName = itemCall.speakerName ? `${itemCall.speakerName}` : undefined;
            if (text && speakerName) {
              // Remove speaker name prefix with various spacing patterns (e.g., "Name:", "Name :", "Name : ")
              const speakerPrefix = new RegExp(`^${speakerName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*`);
              itemCall.cleanText = text.replace(speakerPrefix, '');
            } else {
              itemCall.cleanText = text;
            }
          });

          setGongCallParts(itemsArray);
        }
      })
      .catch((e) => console.log(e));
  };

  const formatDuration = (milliseconds: number): string => {
    const totalMinutes = Math.floor(milliseconds / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  useEffect(() => {

    setRefsIndexes([]);
    

    if (item?.cxItemId) {
      loadGongCall(item?.cxItemId);
    }
  }, [item]);

  return (
    <Box mt={15}>
      {opened && <Modal
        opened={opened}
        centered
        onClose={() => setOpened(false)}
        //closeOnClickOutside={false}
        zIndex={999999}
        size="750px"
        padding={0}
        styles={{
          close: {
            marginRight: "40px", 
            marginTop: "16px",
          },
        }}
        title={<CallTitle cxItem={cxItem} origin={origin} />
        
        }
      >
        <Box sx={{ borderTop: "1px solid #D8D8DB" }} w="100%">
          <ScrollArea
            w={720}
            h={542}
            mah={542}
            p={36}
            offsetScrollbars
            type="always"
            viewportRef={scrollAreaRef}
          >
            <Box>
              <CallTopInfo
                dateDisplay={dateDisplay}
                system={system}
                callDuration={callDuration}
                internalUsers={internalUsers}
                externalUsers={externalUsers}
                account={account}
              />
              {/*  excerpt */}
              {(excerptFirstPartMs && excerptsArr?.length > 0) && (
                <Text
                  color="#5C5CEB"
                  px="sm"
                  my="md"
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // @ts-ignore - nativeEvent may not exist on all targets
                    e.nativeEvent?.stopImmediatePropagation?.();
                    const firstExcerptIndex = refsIndexes[0]
                    scrollToElement(firstExcerptIndex)
                  }}
                  underline
                >
                  Jump to first excerpt
                </Text>
              )}

              {gongCallParts && gongCallParts?.length > 0 ? (
                gongCallParts?.map((callItem: any, index: number) =>  (

                  <Box key={index} ref={callItem?.isExcerpt ? (el) => {refsArr.current[index] = el} : null} bg={callItem.isExcerpt ? "#FFFDD4" : ""}>
                    <GongCallPart
                      callItem={callItem}
                      createdAtDate={createdAtDate}
                    />
                    { (refsIndexes.every((element: number) => index >= element) &&
                      callItem.isLastInExcerpt) ?
                      <Flex 
                        sx={{cursor: 'pointer'}}
                        ml={50}
                        pb='sm' 
                        mt='sm'  
                        onClick={(e) => {
                          e.stopPropagation();
                          // @ts-ignore
                          e.nativeEvent?.stopImmediatePropagation?.();
                          scrollToTop();
                        }}> 
                        <IconArrowUp size={16} color="#5C5CEB"/>
                        <Text ml={4} color="#5C5CEB">Back to top</Text> 
                      </Flex>
                      : !refsIndexes.every((element: number) => index > element) &&
                      callItem.isLastInExcerpt ? 
                        <Flex 
                          sx={{cursor: 'pointer'}}
                          ml={50}
                          pb='sm' 
                          mt='sm'  
                          onClick={(e) => {
                            e.stopPropagation();
                            // @ts-ignore
                            e.nativeEvent?.stopImmediatePropagation?.();
                            const nextExcerptIndex = Array.from(refsIndexes).find((refIndex) => refIndex > index);
                            scrollToElement(nextExcerptIndex)
                          }}> 
                          <IconArrowDown size={16} color="#5C5CEB"/>
                          <Text ml={4} color="#5C5CEB">Next</Text> 
                        </Flex> : <Box></Box>
                    }
                  </Box>
                ) )
              ) : (
                <Flex justify="center">
                  <Text ml={80}>Gong call not available</Text>
                </Flex>
              )}
              {gongCallParts?.length > 0 && (
                <Flex justify="center" fw={600} fz={18} m="md">
                  The End
                </Flex>
              )}
            </Box>
          </ScrollArea>
        </Box>
      </Modal>}
      <CallTextArea
        title={title}
        text={text}
        mlData={item?.ml}
        excerptsArr={excerptsArr}
        setOpened={setOpened}
        type='call'
        isLoadingMl={isLoadingMl}
        originalItemLinkLabel={callLinkLabel}
      />
    </Box>
  );
};

export default GongCall;
